import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Box, Popover, TextField, Button, Typography, CircularProgress } from "@mui/material";
import DataTable from "../Common/Datatable";
import {
  MMNAReportRow,
  getReportColumns,
} from "./mmNaReportConfig";
import AnalyticalAnalysisService, { GetSalesforceFilterPresearchParams } from "../../services/analytics/AnalyticsService";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ColumnFilter, { ExclusiveColumnFilterContext } from "../Common/customFilter";
import { useQueryClient } from "@tanstack/react-query";
import FilterPill from "../Common/FilterPill";
import IntegrationService from "../../services/integrations/Integrations";
import useGlobalContext from "../../context/useGlobalContext";
import { DateRangeValue, SalesforceReportConfig } from "../../utils/types";
import { NumberFilterValue } from "../../utils/utils";
import "../../styles/analytics.css";
export interface FilterOptionItem {
  label: string;
  value: string;
  email?: string;
}

const areArraysEqual = (a: string[] = [], b: string[] = []) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const areObjectsEqual = (a: Record<string, any> = {}, b: Record<string, any> = {}) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => a[key] === b[key]);
};

const extractConfigRawItems = (config: any, ...keys: string[]): any[] => {
  for (const k of keys) {
    const val = config?.[k];
    if (Array.isArray(val)) return val;
    if (typeof val === "string" && val.trim() !== "") return [val.trim()];
    if (val && typeof val === "object") return [val];
  }
  return [];
};

const extractConfigArray = (config: any, ...keys: string[]): string[] => {
  const rawList = extractConfigRawItems(config, ...keys);
  const result: string[] = [];
  rawList.forEach((item) => {
    if (!item) return;
    if (typeof item === "string") {
      const trimmed = item.trim();
      if (trimmed) result.push(trimmed);
    } else if (typeof item === "object") {
      const id = String(item.id || item.value || "").trim();
      if (id) result.push(id);
    }
  });
  return result;
};

const parsePresearchConfigField = (
  config: any,
  onRegister: ((items: (FilterOptionItem | string)[]) => void) | null,
  ...keys: string[]
): string[] => {
  const rawList = extractConfigRawItems(config, ...keys);
  const ids: string[] = [];
  const itemsToRegister: (FilterOptionItem | string)[] = [];
  rawList.forEach((item) => {
    if (!item) return;
    if (typeof item === "string") {
      const val = item.trim();
      if (val) {
        ids.push(val);
        itemsToRegister.push(val);
      }
    } else if (typeof item === "object") {
      const id = String(item.id || item.value || "").trim();
      const name = String(item.name || item.label ).trim();
      const email = item.email ? String(item.email).trim() : undefined;
      if (id) {
        ids.push(id);
        itemsToRegister.push({ label: name, value: id, ...(email && { email }) });
      }
    }
  });
  if (onRegister && itemsToRegister.length > 0) {
    onRegister(itemsToRegister);
  }
  return ids;
};

export interface SavedViewsReportState {
  activeFilterCount: number;
  visibleColumnCount: number;
  hasUnsavedChanges: boolean;
  activeViewName: string | null;
  currentConfigSnapshot: SalesforceReportConfig;
  salesforceReportPref: Record<string, SalesforceReportConfig> | SalesforceReportConfig | null | undefined;
  onSelectView: (viewName: string, viewConfig: SalesforceReportConfig) => void;
  onClearAllFilters: () => void;
}

interface MMNAReportProps {
  onReportStateChange?: (state: SavedViewsReportState) => void;
}

const generateRows = (data: MMNAReportRow[] = []): MMNAReportRow[] => {
  return data?.map((item) => ({
    id: item?.opportunityId,
    opportunityId: item?.opportunityId,
    aeTeam: item?.aeTeam,
    opportunityName: item?.opportunityName,
    arrAmount: item?.arrAmount,
    stage: item?.stage,
    closeDate: item?.closeDate,
    managerForecast: item?.managerForecast,
    marketPodLead: item?.marketPodLead,
    opportunityOwner: item?.opportunityOwner,
    l1Ryg: item?.l1Ryg,
    pricingModelOffered: item?.pricingModelOffered,
    l3TraditionalPricingException: item?.l3TraditionalPricingException,
    managerForecastRationale: item?.managerForecastRationale,
    whatIsOurBiggestHurdle: item?.whatIsOurBiggestHurdle,
    linkToObpCommercialPitchDocument: item?.linkToObpCommercialPitchDocument,
    sellerForecast: item?.sellerForecast,
    buyerMode: item?.buyerMode,
    opportunityRecordType: item?.opportunityRecordType,
    accountName: item?.accountName,
    age: item?.age,
    stageDuration: item?.stageDuration,
    annualRevenue: item?.annualRevenue,
    amount: item?.amount,
    createdDate: item?.createdDate,
    indiaSp: item?.indiaSp,
    spPodLead: item?.spPodLead,
    digitalTransformationPrincipal: item?.digitalTransformationPrincipal,
    opportunitySource: item?.opportunitySource,
    numberOfStrapsExecuted: item?.numberOfStrapsExecuted,
    erpSystemsOthers: item?.erpSystemsOthers,
    dealType: item?.dealType,
    region: item?.region,
    forecastArr: item?.forecastArr,
    eighteenCharacterAccountId: item?.eighteenCharacterAccountId,
    eighteenCharOppId: item?.eighteenCharOppId,
  }));
};

export const getCurrentMonthDateRange = (): DateRangeValue => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return {
    preset: "currentMonth",
    startDate: startOfMonth.toISOString(),
    endDate: endOfMonth.toISOString(),
  };
};

export const isCurrentMonthRange = (startDateStr?: string | null, endDateStr?: string | null): boolean => {
  if (!startDateStr || !endDateStr) return false;
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;
  const now = new Date();
  const isStartCurrentMonth =
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === 1;
  const isEndCurrentMonth =
    end.getFullYear() === now.getFullYear() &&
    end.getMonth() === now.getMonth();
  return isStartCurrentMonth && isEndCurrentMonth;
};

export const isSingleLegacyView = (salesforceReportPref: any): boolean => {
  if (!salesforceReportPref || typeof salesforceReportPref !== "object") return false;
  return (
    "close_date_start" in salesforceReportPref ||
    "close_date_end" in salesforceReportPref ||
    "created_date_start" in salesforceReportPref ||
    "created_date_end" in salesforceReportPref ||
    "stages" in salesforceReportPref ||
    "stage" in salesforceReportPref ||
    "ae_teams" in salesforceReportPref ||
    "ae_team" in salesforceReportPref ||
    "deal_types" in salesforceReportPref ||
    "deal_type" in salesforceReportPref ||
    "record_types" in salesforceReportPref ||
    "record_type" in salesforceReportPref ||
    "manager_forecast" in salesforceReportPref ||
    "manager_forecasts" in salesforceReportPref ||
    "buyer_modes" in salesforceReportPref ||
    "buyer_mode" in salesforceReportPref ||
    "l1_rygs" in salesforceReportPref ||
    "l1_ryg" in salesforceReportPref ||
    "opportunity_sources" in salesforceReportPref ||
    "opportunity_source" in salesforceReportPref ||
    "columns" in salesforceReportPref ||
    "column_visibility" in salesforceReportPref
  );
};

export const parseSavedViewsMap = (
  salesforceReportPref: any
): Record<string, SalesforceReportConfig> => {
  if (!salesforceReportPref || typeof salesforceReportPref !== "object") return {};
  const keys = Object.keys(salesforceReportPref);
  if (keys.length === 0) return {};

  return isSingleLegacyView(salesforceReportPref)
    ? { "Default View": salesforceReportPref as SalesforceReportConfig }
    : (salesforceReportPref as Record<string, SalesforceReportConfig>);
};

const MMNAReport: React.FC<MMNAReportProps> = ({ onReportStateChange }) => {
  const queryClient = useQueryClient();
  const { accountDetails } = useGlobalContext();
  const employeePorId = accountDetails?.profileOrgId ?? "";
  const [rows, setRows] = useState<MMNAReportRow[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [rowCount, setRowCount] = useState<number>(0);
  const { showSnackbar } = useDisplaySnackbar();

  // User Preferences Hooks
  const {
    data: userPreferencesData,
    isSuccess: isUserPreferencesSuccess,
    isError: isUserPreferencesError,
  } = IntegrationService.useGetUserPreferences(
    { employeePorId },
    !!employeePorId
  );

  const salesforceReportPref = useMemo(() => {
    return userPreferencesData?.userPreferences?.salesforce_report;
  }, [userPreferencesData]);

  // Saved Views Map
  const savedViewsMap = useMemo(() => {
    return parseSavedViewsMap(salesforceReportPref);
  }, [salesforceReportPref]);

  // Saved Views State
  const [activeViewName, setActiveViewName] = useState<string | null>(null);
  const [baselineConfig, setBaselineConfig] = useState<SalesforceReportConfig | null>(null);

  // Presearch Query & Options State
  const [presearchQueryState, setPresearchQueryState] = useState<{ columnKey: string; key: string; query: string }>({
    columnKey: "",
    key: "",
    query: "",
  });

  const [opportunityApiOptions, setOpportunityApiOptions] = useState<FilterOptionItem[]>([]);
  const [accountApiOptions, setAccountApiOptions] = useState<FilterOptionItem[]>([]);
  const [marketPodLeadApiOptions, setMarketPodLeadApiOptions] = useState<FilterOptionItem[]>([]);
  const [opportunityOwnerApiOptions, setOpportunityOwnerApiOptions] = useState<FilterOptionItem[]>([]);
  const [indiaSpApiOptions, setIndiaSpApiOptions] = useState<FilterOptionItem[]>([]);
  const [spPodLeadApiOptions, setSpPodLeadApiOptions] = useState<FilterOptionItem[]>([]);

  // Filter Pill States
  const [selectedAeTeams, setSelectedAeTeams] = useState<string[]>([]);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedDealTypes, setSelectedDealTypes] = useState<string[]>([]);
  const [selectedRecordTypes, setSelectedRecordTypes] = useState<string[]>([]);
  const [selectedManagerForecasts, setSelectedManagerForecasts] = useState<string[]>([]);
  const [selectedBuyerModes, setSelectedBuyerModes] = useState<string[]>([]);
  const [selectedL1Rygs, setSelectedL1Rygs] = useState<string[]>([]);
  const [selectedOpportunitySources, setSelectedOpportunitySources] = useState<string[]>([]);
  const [closeDateRange, setCloseDateRange] = useState<DateRangeValue>(() => getCurrentMonthDateRange());
  const [createdDateRange, setCreatedDateRange] = useState<DateRangeValue>({
    preset: "",
    startDate: "",
    endDate: "",
  });
  const [selectedArrAmount, setSelectedArrAmount] = useState<NumberFilterValue | undefined>(undefined);
  const [selectedOpportunityName, setSelectedOpportunityName] = useState<string[]>([]);
  const [selectedMarketPodLead, setSelectedMarketPodLead] = useState<string[]>([]);
  const [selectedOpportunityOwner, setSelectedOpportunityOwner] = useState<string[]>([]);
  const [selectedAccountName, setSelectedAccountName] = useState<string[]>([]);
  const [selectedIndiaSp, setSelectedIndiaSp] = useState<string[]>([]);
  const [selectedSpPodLead, setSelectedSpPodLead] = useState<string[]>([]);

  const knownOptionsRef = React.useRef<Map<string, FilterOptionItem>>(new Map());
  const [knownOptionsVersion, setKnownOptionsVersion] = useState<number>(0);

  const isSalesforceIdString = useCallback((str: string): boolean => {
    if (!str || typeof str !== "string") return false;
    const trimmed = str.trim();
    if (/\s/.test(trimmed)) return false;
    return /^[a-zA-Z0-9]{15,18}$/.test(trimmed) && /^(001|006|005|003|00Q|00O|a0|00D)/i.test(trimmed);
  }, []);

  const registerKnownOption = useCallback((id: string, label: string, email?: string): boolean => {
    if (!id) return false;
    const cleanId = id.trim();
    const cleanLabel = (label || cleanId).trim();

    const existing = knownOptionsRef.current.get(cleanId);

    // Rule: Do not overwrite an existing clean display name with a raw Salesforce ID string
    if (existing && existing.label && !isSalesforceIdString(existing.label) && isSalesforceIdString(cleanLabel)) {
      return false;
    }

    if (!existing || existing.label !== cleanLabel || existing.value !== cleanId || existing.email !== email) {
      const item: FilterOptionItem = {
        label: cleanLabel,
        value: cleanId,
        ...(email && { email }),
      };
      knownOptionsRef.current.set(cleanId, item);
      return true;
    }
    return false;
  }, [isSalesforceIdString]);

  const registerKnownOptions = useCallback(
    (items: (FilterOptionItem | Record<string, any> | string)[]) => {
      let updated = false;
      items.forEach((item) => {
        if (!item) return;
        if (typeof item === "string") {
          if (registerKnownOption(item, item)) updated = true;
        } else if (typeof item === "object") {
          const rawItem = item as Record<string, any>;
          const id = String(rawItem.id || rawItem.value || "").trim();
          const label = String(rawItem.name || rawItem.label).trim();
          const email = rawItem.email ? String(rawItem.email).trim() : undefined;
          if (id) {
            if (registerKnownOption(id, label, email)) updated = true;
          }
        }
      });
      if (updated) {
        setKnownOptionsVersion((v) => v + 1);
      }
      return updated;
    },
    [registerKnownOption]
  );

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const savedOrder = sessionStorage.getItem("mmna_pipeline_columnOrder");
      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);
        if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Error reading mmna_pipeline_columnOrder from sessionStorage", e);
    }
    return [];
  });

  useEffect(() => {
    if (columnOrder && columnOrder.length > 0) {
      sessionStorage.setItem("mmna_pipeline_columnOrder", JSON.stringify(columnOrder));
    }
  }, [columnOrder]);
  const [hasInitializedPrefs, setHasInitializedPrefs] = useState<boolean>(false);

  const appliedPresearchFilters = useMemo(
    () => ({
      stages: selectedStages,
      aeTeams: selectedAeTeams,
      managerForecasts: selectedManagerForecasts,
      recordTypes: selectedRecordTypes,
      dealTypes: selectedDealTypes,
      buyerModes: selectedBuyerModes,
      l1Rygs: selectedL1Rygs,
      opportunitySources: selectedOpportunitySources,
      closeDateStart: closeDateRange?.startDate || undefined,
      closeDateEnd: closeDateRange?.endDate || undefined,
      createdDateStart: createdDateRange?.startDate || undefined,
      createdDateEnd: createdDateRange?.endDate || undefined,
      ...(selectedArrAmount?.value !== undefined && Boolean(selectedArrAmount?.operator) && {
        arrAmount: selectedArrAmount.value,
        arrAmountOperator: selectedArrAmount.operator,
      }),
    }),
    [
      selectedStages,
      selectedAeTeams,
      selectedManagerForecasts,
      selectedRecordTypes,
      selectedDealTypes,
      selectedBuyerModes,
      selectedL1Rygs,
      selectedOpportunitySources,
      closeDateRange,
      createdDateRange,
      selectedArrAmount,
    ]
  );

  const presearchParams = useMemo<GetSalesforceFilterPresearchParams>(
    () => {
      const isLiveSearch = Boolean(presearchQueryState.query.trim());
      const includeFilters = presearchQueryState.key === "opportunity" || presearchQueryState.key === "account";

      if (isLiveSearch) {
        return {
          key: presearchQueryState.key,
          query: presearchQueryState.query.trim(),
          ...(includeFilters && appliedPresearchFilters),
        };
      }

      let ids: string[] = [];
      if (presearchQueryState.key === "user") {
        ids = Array.from(
          new Set(
            [
              ...selectedMarketPodLead,
              ...selectedOpportunityOwner,
              ...selectedIndiaSp,
              ...selectedSpPodLead,
            ].filter(Boolean)
          )
        );
      } else if (presearchQueryState.key === "account") {
        ids = Array.from(new Set((selectedAccountName || []).filter(Boolean)));
      } else if (presearchQueryState.key === "opportunity") {
        ids = Array.from(new Set((selectedOpportunityName || []).filter(Boolean)));
      }

      return {
        key: presearchQueryState.key,
        ids: ids.length > 0 ? ids : undefined,
        ...(includeFilters && appliedPresearchFilters),
      };
    },
    [
      presearchQueryState.key,
      presearchQueryState.query,
      appliedPresearchFilters,
      selectedMarketPodLead,
      selectedOpportunityOwner,
      selectedIndiaSp,
      selectedSpPodLead,
      selectedAccountName,
      selectedOpportunityName,
    ]
  );

  const {
    data: filtersPresearchData,
    isSuccess: isFiltersPreserchSuccess,
    isLoading: isFiltersPresearchLoading,
    isFetching: isFiltersPresearchFetching,
    isError: isFiltersPresearchError,
    error: filtersPresearchError,
  } = AnalyticalAnalysisService.useGetSalesforceFilterPresearch(
    presearchParams,
    Boolean(
      presearchQueryState.key &&
        ((presearchQueryState.query && presearchQueryState.query.trim() !== "") ||
          (presearchParams.ids && presearchParams.ids?.length > 0))
    )
  );

  const isPresearchLoadingRef = React.useRef(false);
  isPresearchLoadingRef.current = Boolean(isFiltersPresearchLoading || isFiltersPresearchFetching);

  const presearchColKeyRef = React.useRef("");
  presearchColKeyRef.current = presearchQueryState.columnKey;

  const presearchHasQueryRef = React.useRef(false);
  presearchHasQueryRef.current = Boolean(presearchQueryState.key);

  const isColumnPresearchLoading = useCallback((colKey: string) => {
    return isPresearchLoadingRef.current && presearchColKeyRef.current === colKey && presearchHasQueryRef.current;
  }, []);

  useEffect(() => {
    if (isFiltersPresearchError) {
      showSnackbar("error", filtersPresearchError?.message || "Failed to load presearch options.");
    }
  }, [isFiltersPresearchError, filtersPresearchError, showSnackbar]);

  useEffect(() => {
    if (isFiltersPreserchSuccess && filtersPresearchData) {
      const extractOptionsList = (resData: any): FilterOptionItem[] => {
        if (!resData) return [];

        const rawList: any[] = Array.isArray(resData)
          ? resData
          : Array.isArray(resData?.data)
          ? resData.data
          : Array.isArray(resData?.data?.data)
          ? resData.data.data
          : [];

        const result: FilterOptionItem[] = [];
        const seenValues = new Set<string>();

        rawList.forEach((item) => {
          if (!item) return;
          if (typeof item === "string") {
            const val = item.trim();
            if (val && !seenValues.has(val)) {
              seenValues.add(val);
              result.push({ label: val, value: val });
            }
          } else if (typeof item === "object") {
            const id = String(item.id || item.value || "").trim();
            const name = String(item.name || item.label).trim();
            const email = item.email ? String(item.email).trim() : undefined;
            if (id && !seenValues.has(id)) {
              seenValues.add(id);
              result.push({ label: name, value: id, ...(email && { email }) });
            }
          }
        });

        return result;
      };

      const list = extractOptionsList(filtersPresearchData);
      registerKnownOptions(list);
      const colKey = presearchQueryState.columnKey;
      if (colKey === "opportunityName") {
        setOpportunityApiOptions(list);
      } else if (colKey === "accountName") {
        setAccountApiOptions(list);
      } else if (colKey === "marketPodLead") {
        setMarketPodLeadApiOptions(list);
      } else if (colKey === "opportunityOwner") {
        setOpportunityOwnerApiOptions(list);
      } else if (colKey === "indiaSp") {
        setIndiaSpApiOptions(list);
      } else if (colKey === "spPodLead") {
        setSpPodLeadApiOptions(list);
      }

      if (presearchQueryState.key && !presearchQueryState.query.trim()) {
        setPresearchQueryState({ columnKey: "", key: "", query: "" });
      }
    }
  }, [isFiltersPreserchSuccess, filtersPresearchData, presearchQueryState.columnKey, presearchQueryState.key, presearchQueryState.query, registerKnownOptions]);

  useEffect(() => {
    if (!hasInitializedPrefs || presearchQueryState.key) return;

    const checkCategory = (selectedList: string[], columnKey: string, key: string) => {
      if (!selectedList || selectedList.length === 0) return null;
      const hasUnhydrated = selectedList.some((id) => {
        if (!id) return false;
        const known = knownOptionsRef.current.get(id);
        return !known || known.label === id || isSalesforceIdString(known.label);
      });
      if (hasUnhydrated) {
        return { columnKey, key };
      }
      return null;
    };

    const target =
      checkCategory(selectedOpportunityName, "opportunityName", "opportunity") ||
      checkCategory(selectedAccountName, "accountName", "account") ||
      checkCategory(selectedMarketPodLead, "marketPodLead", "user") ||
      checkCategory(selectedOpportunityOwner, "opportunityOwner", "user") ||
      checkCategory(selectedIndiaSp, "indiaSp", "user") ||
      checkCategory(selectedSpPodLead, "spPodLead", "user");

    if (target && !presearchQueryState.key) {
      setPresearchQueryState({ columnKey: target.columnKey, key: target.key, query: "" });
    }
  }, [
    hasInitializedPrefs,
    presearchQueryState.key,
    selectedOpportunityName,
    selectedAccountName,
    selectedMarketPodLead,
    selectedOpportunityOwner,
    selectedIndiaSp,
    selectedSpPodLead,
  ]);

  const handleSearchChange = useCallback((columnKey: string, query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      setPresearchQueryState({
        columnKey: "",
        key: "",
        query: "",
      });

      return;
    }

    const entityKeyMap: Record<string, string> = {
      opportunityName: "opportunity",
      accountName: "account",
    };
    const key = entityKeyMap[columnKey] || "user";

    if (columnKey === "opportunityName") setOpportunityApiOptions([]);
    else if (columnKey === "accountName") setAccountApiOptions([]);
    else if (columnKey === "marketPodLead") setMarketPodLeadApiOptions([]);
    else if (columnKey === "opportunityOwner") setOpportunityOwnerApiOptions([]);
    else if (columnKey === "indiaSp") setIndiaSpApiOptions([]);
    else if (columnKey === "spPodLead") setSpPodLeadApiOptions([]);

    setPresearchQueryState({ columnKey, key, query: trimmed });
  }, []);
  const [sortingState, setSortingState] = useState<{ sort_field: string; sort_order: "asc" | "desc" }>({
    sort_field: "closeDate",
    sort_order: "desc",
  });
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const exclusiveFilterValue = useMemo(
    () => ({
      activeKey: openFilterColumn,
      setActiveKey: setOpenFilterColumn,
    }),
    [openFilterColumn]
  );

  const applySavedView = useCallback((name: string, viewConfig: SalesforceReportConfig) => {
    if (!viewConfig) return;

    setSelectedAeTeams(extractConfigArray(viewConfig, "ae_teams"));
    setSelectedStages(extractConfigArray(viewConfig, "stages"));
    setSelectedDealTypes(extractConfigArray(viewConfig, "deal_types"));
    setSelectedRecordTypes(extractConfigArray(viewConfig, "record_types"));
    setSelectedManagerForecasts(extractConfigArray(viewConfig, "manager_forecast"));
    setSelectedBuyerModes(extractConfigArray(viewConfig, "buyer_modes"));
    setSelectedL1Rygs(extractConfigArray(viewConfig, "l1_rygs"));
    setSelectedOpportunitySources(extractConfigArray(viewConfig, "opportunity_sources"));

    if (isCurrentMonthRange(viewConfig?.close_date_start, viewConfig?.close_date_end)) {
      setCloseDateRange(getCurrentMonthDateRange());
    } else if (viewConfig?.close_date_start && viewConfig?.close_date_end) {
      setCloseDateRange({
        preset: "custom",
        startDate: viewConfig.close_date_start,
        endDate: viewConfig.close_date_end,
      });
    } else {
      setCloseDateRange(getCurrentMonthDateRange());
    }

    if (viewConfig?.created_date_start && viewConfig?.created_date_end) {
      setCreatedDateRange({
        preset: "custom",
        startDate: viewConfig.created_date_start,
        endDate: viewConfig.created_date_end,
      });
    } else {
      setCreatedDateRange({ preset: "", startDate: "", endDate: "" });
    }

    if (
      viewConfig?.arr_amount !== undefined &&
      viewConfig?.arr_amount !== null &&
      viewConfig?.arr_amount_operator !== undefined &&
      viewConfig?.arr_amount_operator !== null
    ) {
      setSelectedArrAmount({
        value: viewConfig.arr_amount,
        operator: viewConfig.arr_amount_operator as "gt" | "lt",
      });
    } else {
      setSelectedArrAmount(undefined);
    }

    setSelectedOpportunityName(parsePresearchConfigField(viewConfig, registerKnownOptions, "opportunity_ids", "opportunity_name"));
    setSelectedMarketPodLead(parsePresearchConfigField(viewConfig, registerKnownOptions, "market_pod_lead"));
    setSelectedOpportunityOwner(parsePresearchConfigField(viewConfig, registerKnownOptions, "opportunity_owner"));
    setSelectedAccountName(parsePresearchConfigField(viewConfig, registerKnownOptions, "account_ids", "account_name"));
    setSelectedIndiaSp(parsePresearchConfigField(viewConfig, registerKnownOptions, "india_sp"));
    setSelectedSpPodLead(parsePresearchConfigField(viewConfig, registerKnownOptions, "sp_pod_lead"));

    if (viewConfig?.columns && typeof viewConfig.columns === "object" && !Array.isArray(viewConfig.columns)) {
      setColumnVisibility(viewConfig.columns);
    } else if (Array.isArray(viewConfig?.columns)) {
      const vis: Record<string, boolean> = {};
      viewConfig.columns.forEach((k: string) => {
        vis[k] = true;
      });
      setColumnVisibility(vis);
    } else {
      setColumnVisibility({});
    }

    setActiveViewName(name);
    setBaselineConfig(viewConfig);
  }, []);

  const handleSortingChange = useCallback((sorting: any[]) => {
    if (sorting && sorting?.length > 0) {
      setSortingState({
        sort_field: sorting[0]?.id,
        sort_order: sorting[0]?.desc ? "desc" : "asc",
      });
    } else {
      setSortingState({
        sort_field: "closeDate",
        sort_order: "desc",
      });
    }
  }, []);

  useEffect(() => {
    if (hasInitializedPrefs) return;

    if (!employeePorId || isUserPreferencesError) {
      setHasInitializedPrefs(true);
      return;
    }

    if (isUserPreferencesSuccess) {
      if (salesforceReportPref) {
        const keys = Object.keys(savedViewsMap);
        if (keys.length > 0) {
          const firstViewName = keys[0];
          applySavedView(firstViewName, savedViewsMap[firstViewName]);
        }
      }
      setHasInitializedPrefs(true);
    }
  }, [
    employeePorId,
    isUserPreferencesSuccess,
    isUserPreferencesError,
    salesforceReportPref,
    savedViewsMap,
    hasInitializedPrefs,
    applySavedView,
  ]);

  // Filter options from API
  const { data: filterOptionsData } =
    AnalyticalAnalysisService.useGetSalesforceOpportunityReportFilters(true);

  const filterOptionsObj = useMemo(() => {
    return (
      filterOptionsData?.data?.salesforce_mma_filter
    );
  }, [filterOptionsData]);

  // Filter options from API only (avoid flicker from current-page row values)
  const aeTeamOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.ae_team || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const stageOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.stages || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const dealTypeOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.deal_type || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const recordTypeOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.record_types || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const managerForecastOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.manager_forecast || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const buyerModeOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.buyer_mode || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const l1RygOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.l1_ryg || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const opportunitySourceOptions = useMemo(() => {
    const apiOptions: string[] = filterOptionsObj?.opportunity_source || [];
    return apiOptions.map((v) => ({ label: v, value: v }));
  }, [filterOptionsObj]);

  const formatFilterOptionsList = useCallback((apiList: FilterOptionItem[], selectedList: string[]) => {
    const optionsMap = new Map<string, FilterOptionItem>();

    apiList.forEach((item) => {
      if (item.value) {
        const known = knownOptionsRef.current.get(item.value);
        const resolvedLabel =
          known?.label && !isSalesforceIdString(known.label)
            ? known.label
            : item.label && !isSalesforceIdString(item.label)
            ? item.label
            : item.value;

        optionsMap.set(item.value, {
          ...item,
          label: resolvedLabel,
          ...(known?.email && { email: known.email }),
        });
      }
    });

    selectedList.forEach((val) => {
      if (val) {
        const known = knownOptionsRef.current.get(val);
        const existingInMap = optionsMap.get(val);

        const resolvedLabel =
          known?.label && !isSalesforceIdString(known.label)
            ? known.label
            : existingInMap?.label && !isSalesforceIdString(existingInMap.label)
            ? existingInMap.label
            : val;

        optionsMap.set(val, {
          value: val,
          label: resolvedLabel,
          ...(known?.email && { email: known.email }),
        });
      }
    });

    return Array.from(optionsMap.values());
  }, []);

  const opportunityNameOptions = useMemo(
    () => formatFilterOptionsList(opportunityApiOptions, selectedOpportunityName),
    [opportunityApiOptions, selectedOpportunityName, knownOptionsVersion]
  );

  const accountNameOptions = useMemo(
    () => formatFilterOptionsList(accountApiOptions, selectedAccountName),
    [accountApiOptions, selectedAccountName, knownOptionsVersion]
  );

  const marketPodLeadOptions = useMemo(
    () => formatFilterOptionsList(marketPodLeadApiOptions, selectedMarketPodLead),
    [marketPodLeadApiOptions, selectedMarketPodLead, knownOptionsVersion]
  );

  const opportunityOwnerOptions = useMemo(
    () => formatFilterOptionsList(opportunityOwnerApiOptions, selectedOpportunityOwner),
    [opportunityOwnerApiOptions, selectedOpportunityOwner, knownOptionsVersion]
  );

  const indiaSpOptions = useMemo(
    () => formatFilterOptionsList(indiaSpApiOptions, selectedIndiaSp),
    [indiaSpApiOptions, selectedIndiaSp, knownOptionsVersion]
  );

  const spPodLeadOptions = useMemo(
    () => formatFilterOptionsList(spPodLeadApiOptions, selectedSpPodLead),
    [spPodLeadApiOptions, selectedSpPodLead, knownOptionsVersion]
  );

  // Popover state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editingInfo, setEditingInfo] = useState<{
    rowId: string;
    columnId: keyof MMNAReportRow;
    columnName: string;
    value: string;
  } | null>(null);

  // Hook for updating opportunity report in Salesforce
  const updateMutation = AnalyticalAnalysisService.useUpdateAnalyticsSalesforceOpportunityReport();
  const updateOpportunity = updateMutation.mutate;
  const isUpdating = updateMutation.isPending;

  const opportunityReportParams = useMemo(
    () => ({
      limit: pageSize,
      offset: pageNumber,
      sortBy: sortingState.sort_field,
      sortOrder: sortingState.sort_order,
      stages: selectedStages,
      aeTeams: selectedAeTeams,
      managerForecasts: selectedManagerForecasts,
      recordTypes: selectedRecordTypes,
      dealTypes: selectedDealTypes,
      buyerModes: selectedBuyerModes,
      l1Rygs: selectedL1Rygs,
      opportunitySources: selectedOpportunitySources,
      closeDateStart: closeDateRange?.startDate || undefined,
      closeDateEnd: closeDateRange?.endDate || undefined,
      createdDateStart: createdDateRange?.startDate || undefined,
      createdDateEnd: createdDateRange?.endDate || undefined,
      ...(selectedArrAmount?.value !== undefined && Boolean(selectedArrAmount?.operator) && {
        arrAmount: selectedArrAmount.value,
        arrAmountOperator: selectedArrAmount.operator,
      }),
      ...(selectedOpportunityName.length > 0 && {
        opportunityName: selectedOpportunityName,
      }),
      ...(selectedMarketPodLead.length > 0 && {
        marketPodLead: selectedMarketPodLead,
      }),
      ...(selectedOpportunityOwner.length > 0 && {
        opportunityOwner: selectedOpportunityOwner,
      }),
      ...(selectedAccountName.length > 0 && {
        accountName: selectedAccountName,
      }),
      ...(selectedIndiaSp.length > 0 && {
        indiaSp: selectedIndiaSp,
      }),
      ...(selectedSpPodLead.length > 0 && {
        spPodLead: selectedSpPodLead,
      }),
    }),
    [
      pageSize,
      pageNumber,
      sortingState,
      selectedStages,
      selectedAeTeams,
      selectedManagerForecasts,
      selectedRecordTypes,
      selectedDealTypes,
      selectedBuyerModes,
      selectedL1Rygs,
      selectedOpportunitySources,
      closeDateRange,
      createdDateRange,
      selectedArrAmount,
      selectedOpportunityName,
      selectedMarketPodLead,
      selectedOpportunityOwner,
      selectedAccountName,
      selectedIndiaSp,
      selectedSpPodLead,
    ]
  );

  const {
    isLoading: isOpportunityReportLoading,
    isFetching: isOpportunityReportFetching,
    isSuccess: isOpportunityReportSuccess,
    isError: isOpportunityReportError,
    error: OpportunityReportError,
    data: OpportunityReportData,
  } = AnalyticalAnalysisService.useGetAnalyticsSalesforceOpportunityReport(
    opportunityReportParams,
    hasInitializedPrefs
  );

  useEffect(() => {
    if (isOpportunityReportSuccess && OpportunityReportData) {
      const list = OpportunityReportData?.data?.data || [];
      const mapped = generateRows(list);
      setRows(mapped);
      setRowCount(OpportunityReportData?.data?.totalRecords || mapped?.length);
    }
    if (isOpportunityReportError) {
      showSnackbar("error", OpportunityReportError?.message || "Failed to load data.");
    }
  }, [isOpportunityReportSuccess, isOpportunityReportError, OpportunityReportError, OpportunityReportData]);

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
    setEditingInfo(null);
  }, []);

  // Function to save edited values
  const handleSaveCell = useCallback(
    (rowId: string, columnId: keyof MMNAReportRow, newValue: string) => {
      const row = rows.find((r: MMNAReportRow) => r.opportunityId === rowId);
      if (!row) return;

      // Skip update if value is unchanged
      const currentValue = row[columnId] ?? "";
      if (currentValue === newValue) {
        handleClosePopover();
        return;
      }

      // Optimistic update of local state
      setRows((prevRows) =>
        prevRows.map((r: MMNAReportRow) => {
          if (r.opportunityId === rowId) {
            return {
              ...r,
              [columnId]: newValue,
            };
          }
          return r;
        })
      );

      // Trigger background mutation call
      updateOpportunity({
        opportunityId: row.opportunityId,
        [columnId]: newValue,
      }, {
        onSuccess: () => {
          showSnackbar("success", "Opportunity updated successfully!");
          queryClient.invalidateQueries({
            queryKey: ["get_analytics_salesforce_opportunity_report"],
          });
          handleClosePopover();
        },
        onError: (err: any) => {
          showSnackbar("error", err?.message || "Failed to update opportunity.");
          // Revert optimistic update
          setRows((prevRows) =>
            prevRows.map((r: MMNAReportRow) => {
              if (r.opportunityId === rowId) {
                return {
                  ...r,
                  [columnId]: row[columnId],
                };
              }
              return r;
            })
          );
          queryClient.invalidateQueries({
            queryKey: ["get_analytics_salesforce_opportunity_report"],
          });
        }
      });
    },
    [rows, updateOpportunity, queryClient, showSnackbar, handleClosePopover]
  );

  // Handle edit click to open the Popover
  const handleEditClick = (
    anchorEl: HTMLElement,
    rowId: string,
    columnId: keyof MMNAReportRow,
    columnName: string,
    value: string
  ) => {
    setAnchorEl(anchorEl);
    setEditingInfo({
      rowId,
      columnId,
      columnName,
      value,
    });
  };

  const handleSavePopover = useCallback(() => {
    if (editingInfo) {
      handleSaveCell(editingInfo.rowId, editingInfo.columnId, editingInfo.value);
    }
  }, [editingInfo, handleSaveCell]);

  const reportFilters = useMemo(
    () => ({
      aeTeam: selectedAeTeams,
      stage: selectedStages,
      managerForecast: selectedManagerForecasts,
      dealType: selectedDealTypes,
      opportunityRecordType: selectedRecordTypes,
      buyerMode: selectedBuyerModes,
      l1Ryg: selectedL1Rygs,
      opportunitySource: selectedOpportunitySources,
      closeDate: closeDateRange,
      createdDate: createdDateRange,
      arrAmount: selectedArrAmount,
      opportunityName: selectedOpportunityName,
      marketPodLead: selectedMarketPodLead,
      opportunityOwner: selectedOpportunityOwner,
      accountName: selectedAccountName,
      indiaSp: selectedIndiaSp,
      spPodLead: selectedSpPodLead,
    }),
    [
      selectedAeTeams,
      selectedStages,
      selectedManagerForecasts,
      selectedDealTypes,
      selectedRecordTypes,
      selectedBuyerModes,
      selectedL1Rygs,
      selectedOpportunitySources,
      closeDateRange,
      createdDateRange,
      selectedArrAmount,
      selectedOpportunityName,
      selectedMarketPodLead,
      selectedOpportunityOwner,
      selectedAccountName,
      selectedIndiaSp,
      selectedSpPodLead,
    ]
  );

  const handleColumnFilterChange = useCallback((columnKey: string, value: any) => {
    const arrayFilterSetters: Record<string, React.Dispatch<React.SetStateAction<string[]>>> = {
      aeTeam: setSelectedAeTeams,
      stage: setSelectedStages,
      managerForecast: setSelectedManagerForecasts,
      dealType: setSelectedDealTypes,
      opportunityRecordType: setSelectedRecordTypes,
      buyerMode: setSelectedBuyerModes,
      l1Ryg: setSelectedL1Rygs,
      opportunitySource: setSelectedOpportunitySources,
      opportunityName: setSelectedOpportunityName,
      marketPodLead: setSelectedMarketPodLead,
      opportunityOwner: setSelectedOpportunityOwner,
      accountName: setSelectedAccountName,
      indiaSp: setSelectedIndiaSp,
      spPodLead: setSelectedSpPodLead,
    };

    if (arrayFilterSetters[columnKey]) {
      const setter = arrayFilterSetters[columnKey];
      setter(Array.isArray(value) ? value : value ? [value] : []);
    } else if (columnKey === "closeDate") {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        setCloseDateRange(value as DateRangeValue);
      } else {
        setCloseDateRange({ preset: "", startDate: "", endDate: "" });
      }
    } else if (columnKey === "createdDate") {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        setCreatedDateRange(value as DateRangeValue);
      } else {
        setCreatedDateRange({ preset: "", startDate: "", endDate: "" });
      }
    } else if (columnKey === "arrAmount") {
      if (value && typeof value === "object" && "operator" in value && "value" in value) {
        setSelectedArrAmount(value as NumberFilterValue);
      } else {
        setSelectedArrAmount(undefined);
      }
    }
  }, []);

  const reportFilterOptions = useMemo(
    () => ({
      aeTeam: aeTeamOptions,
      stage: stageOptions,
      managerForecast: managerForecastOptions,
      dealType: dealTypeOptions,
      opportunityRecordType: recordTypeOptions,
      buyerMode: buyerModeOptions,
      l1Ryg: l1RygOptions,
      opportunitySource: opportunitySourceOptions,
      opportunityName: opportunityNameOptions,
      accountName: accountNameOptions,
      marketPodLead: marketPodLeadOptions,
      opportunityOwner: opportunityOwnerOptions,
      indiaSp: indiaSpOptions,
      spPodLead: spPodLeadOptions,
    }),
    [
      aeTeamOptions,
      stageOptions,
      managerForecastOptions,
      dealTypeOptions,
      recordTypeOptions,
      buyerModeOptions,
      l1RygOptions,
      opportunitySourceOptions,
      opportunityNameOptions,
      accountNameOptions,
      marketPodLeadOptions,
      opportunityOwnerOptions,
      indiaSpOptions,
      spPodLeadOptions,
    ]
  );

  // Generate columns with the handler passed in
  const columns = useMemo(
    () =>
      getReportColumns(
        handleEditClick,
        editingInfo,
        reportFilters,
        handleColumnFilterChange,
        reportFilterOptions,
        openFilterColumn,
        setOpenFilterColumn,
        handleSearchChange,
        isColumnPresearchLoading
      ),
    [
      handleEditClick,
      editingInfo,
      reportFilters,
      handleColumnFilterChange,
      reportFilterOptions,
      openFilterColumn,
      handleSearchChange,
      isColumnPresearchLoading
    ]
  );

  const isPopoverOpen = Boolean(anchorEl);

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage+1);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const showSpinner = isOpportunityReportLoading || isOpportunityReportFetching;
  const [pinnedColumns] = useState({
    left: ['aeTeam', 'opportunityName', 'arrAmount',],
  });

  const reconciledColumnOrder = useMemo(() => {
    const validAccessorKeys = columns.map((col: any) => col.accessorKey).filter(Boolean);
    if (validAccessorKeys.length === 0) return [];
    if (!columnOrder || columnOrder.length === 0) return validAccessorKeys;
    const existingSaved = columnOrder.filter((key) => validAccessorKeys.includes(key));
    const missingKeys = validAccessorKeys.filter((key) => !existingSaved.includes(key));
    return [...existingSaved, ...missingKeys];
  }, [columns, columnOrder]);

  const columnPickerOptions = useMemo(() => {
    const defaultTrueColumns = ["aeTeam", "opportunityName", "arrAmount"];
    const filteredCols = columns.filter((col: any) => col.accessorKey);

    if (reconciledColumnOrder && reconciledColumnOrder.length > 0) {
      filteredCols.sort((a: any, b: any) => {
        const indexA = reconciledColumnOrder.indexOf(a.accessorKey);
        const indexB = reconciledColumnOrder.indexOf(b.accessorKey);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    return filteredCols.map((col: any) => {
      const isAlwaysVisible = defaultTrueColumns.includes(col.accessorKey);
      return {
        value: col.accessorKey,
        label: col.header,
        ...(isAlwaysVisible && { defaultValue: true, isPinned: true }),
      };
    });
  }, [columns, reconciledColumnOrder]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    const arrayFilters = [
      selectedStages,
      selectedAeTeams,
      selectedDealTypes,
      selectedRecordTypes,
      selectedManagerForecasts,
      selectedBuyerModes,
      selectedL1Rygs,
      selectedOpportunitySources,
      selectedOpportunityName,
      selectedMarketPodLead,
      selectedOpportunityOwner,
      selectedAccountName,
      selectedIndiaSp,
      selectedSpPodLead,
    ];
    count += arrayFilters.filter((arr) => arr?.length > 0).length;

    if (closeDateRange?.preset && closeDateRange.preset !== "currentMonth") count++;
    if (createdDateRange?.startDate && createdDateRange?.endDate) count++;
    if (selectedArrAmount?.value !== undefined && Boolean(selectedArrAmount?.operator)) count++;

    return count;
  }, [
    selectedStages,
    selectedAeTeams,
    selectedDealTypes,
    selectedRecordTypes,
    selectedManagerForecasts,
    selectedBuyerModes,
    selectedL1Rygs,
    selectedOpportunitySources,
    closeDateRange,
    createdDateRange,
    selectedArrAmount,
    selectedOpportunityName,
    selectedMarketPodLead,
    selectedOpportunityOwner,
    selectedAccountName,
    selectedIndiaSp,
    selectedSpPodLead,
  ]);

  const visibleColumnCount = useMemo(() => {
    const allAccessors = columns.filter((col: any) => col.accessorKey).map((col: any) => col.accessorKey);
    if (Object.keys(columnVisibility).length === 0) {
      return columnPickerOptions.filter((col: any) => col.defaultValue !== false).length;
    }
    return allAccessors.filter((key: string) => columnVisibility[key] !== false).length;
  }, [columns, columnVisibility, columnPickerOptions]);

  const hasUnsavedChanges = useMemo(() => {
    if (!baselineConfig) {
      return activeFilterCount > 0 || Object.keys(columnVisibility).length > 0;
    }

    const arrayChecks = [
      [selectedAeTeams, extractConfigArray(baselineConfig, "ae_teams")],
      [selectedStages, extractConfigArray(baselineConfig, "stages")],
      [selectedDealTypes, extractConfigArray(baselineConfig, "deal_types")],
      [selectedRecordTypes, extractConfigArray(baselineConfig, "record_types")],
      [selectedManagerForecasts, extractConfigArray(baselineConfig, "manager_forecast")],
      [selectedBuyerModes, extractConfigArray(baselineConfig, "buyer_modes")],
      [selectedL1Rygs, extractConfigArray(baselineConfig, "l1_rygs")],
      [selectedOpportunitySources, extractConfigArray(baselineConfig, "opportunity_sources")],
      [selectedOpportunityName, extractConfigArray(baselineConfig, "opportunity_ids")],
      [selectedMarketPodLead, extractConfigArray(baselineConfig, "market_pod_lead")],
      [selectedOpportunityOwner, extractConfigArray(baselineConfig, "opportunity_owner")],
      [selectedAccountName, extractConfigArray(baselineConfig, "account_ids")],
      [selectedIndiaSp, extractConfigArray(baselineConfig, "india_sp")],
      [selectedSpPodLead, extractConfigArray(baselineConfig, "sp_pod_lead")],
    ];
    if (arrayChecks.some(([curr, base]) => !areArraysEqual(curr, base))) return true;

    const defaultCloseRange = getCurrentMonthDateRange();
    const baseCloseStart = baselineConfig?.close_date_start || defaultCloseRange.startDate;
    const baseCloseEnd = baselineConfig?.close_date_end || defaultCloseRange.endDate;
    if ((closeDateRange?.startDate || "") !== (baseCloseStart || "")) return true;
    if ((closeDateRange?.endDate || "") !== (baseCloseEnd || "")) return true;
    if ((createdDateRange?.startDate || "") !== (baselineConfig?.created_date_start || "")) return true;
    if ((createdDateRange?.endDate || "") !== (baselineConfig?.created_date_end || "")) return true;

    const currentArrVal = selectedArrAmount?.operator ? selectedArrAmount?.value : undefined;
    const currentArrOp = selectedArrAmount?.operator || null;
    const baseArrVal = baselineConfig?.arr_amount_operator ? baselineConfig?.arr_amount : undefined;
    const baseArrOp = baselineConfig?.arr_amount_operator || null;
    if (currentArrVal !== baseArrVal || currentArrOp !== baseArrOp) return true;

    const baseColVisRaw = baselineConfig?.columns || {};
    const baseColVis: Record<string, boolean> = Array.isArray(baseColVisRaw)
      ? baseColVisRaw.reduce((acc: Record<string, boolean>, col: string) => ({ ...acc, [col]: true }), {})
      : baseColVisRaw;
    if (!areObjectsEqual(columnVisibility, baseColVis)) return true;

    return false;
  }, [
    baselineConfig,
    activeFilterCount,
    selectedAeTeams,
    selectedStages,
    selectedDealTypes,
    selectedRecordTypes,
    selectedManagerForecasts,
    selectedBuyerModes,
    selectedL1Rygs,
    selectedOpportunitySources,
    closeDateRange,
    createdDateRange,
    selectedArrAmount,
    selectedOpportunityName,
    selectedMarketPodLead,
    selectedOpportunityOwner,
    selectedAccountName,
    selectedIndiaSp,
    selectedSpPodLead,
    columnVisibility,
  ]);

  const mapSelectedToPersistedObjects = useCallback((selectedIds: string[]) => {
    if (!selectedIds || selectedIds.length === 0) return null;
    return selectedIds;
  }, []);

  const currentConfigSnapshot = useMemo(
    () => ({
      stages: selectedStages,
      ae_teams: selectedAeTeams,
      deal_types: selectedDealTypes,
      record_types: selectedRecordTypes,
      manager_forecast: selectedManagerForecasts,
      buyer_modes: selectedBuyerModes.length > 0 ? selectedBuyerModes : null,
      l1_rygs: selectedL1Rygs.length > 0 ? selectedL1Rygs : null,
      opportunity_sources: selectedOpportunitySources.length > 0 ? selectedOpportunitySources : null,
      close_date_start: closeDateRange?.startDate || null,
      close_date_end: closeDateRange?.endDate || null,
      created_date_start: createdDateRange?.startDate || null,
      created_date_end: createdDateRange?.endDate || null,
      ...(selectedArrAmount?.value !== undefined && Boolean(selectedArrAmount?.operator)
        ? {
            arr_amount: selectedArrAmount.value,
            arr_amount_operator: selectedArrAmount.operator,
          }
        : {
            arr_amount: null,
            arr_amount_operator: null,
          }),
      opportunity_ids: mapSelectedToPersistedObjects(selectedOpportunityName),
      market_pod_lead: mapSelectedToPersistedObjects(selectedMarketPodLead),
      opportunity_owner: mapSelectedToPersistedObjects(selectedOpportunityOwner),
      account_ids: mapSelectedToPersistedObjects(selectedAccountName),
      india_sp: mapSelectedToPersistedObjects(selectedIndiaSp),
      sp_pod_lead: mapSelectedToPersistedObjects(selectedSpPodLead),
      columns: columnVisibility,
    }),
    [
      selectedStages,
      selectedAeTeams,
      selectedDealTypes,
      selectedRecordTypes,
      selectedManagerForecasts,
      selectedBuyerModes,
      selectedL1Rygs,
      selectedOpportunitySources,
      closeDateRange,
      createdDateRange,
      selectedArrAmount,
      selectedOpportunityName,
      selectedMarketPodLead,
      selectedOpportunityOwner,
      selectedAccountName,
      selectedIndiaSp,
      selectedSpPodLead,
      columnVisibility,
      mapSelectedToPersistedObjects,
    ]
  );

  const handleClearAllFilters = useCallback(() => {
    setCloseDateRange(getCurrentMonthDateRange());
    setCreatedDateRange({ preset: "", startDate: "", endDate: "" });
    setSelectedAeTeams([]);
    setSelectedStages([]);
    setSelectedDealTypes([]);
    setSelectedRecordTypes([]);
    setSelectedManagerForecasts([]);
    setSelectedBuyerModes([]);
    setSelectedL1Rygs([]);
    setSelectedOpportunitySources([]);
    setSelectedArrAmount(undefined);
    setSelectedOpportunityName([]);
    setSelectedMarketPodLead([]);
    setSelectedOpportunityOwner([]);
    setSelectedAccountName([]);
    setSelectedIndiaSp([]);
    setSelectedSpPodLead([]);
    setColumnVisibility({});
    setActiveViewName(null);
    setBaselineConfig(null);
  }, []);

  useEffect(() => {
    if (onReportStateChange) {
      onReportStateChange({
        activeFilterCount,
        visibleColumnCount,
        hasUnsavedChanges,
        activeViewName,
        currentConfigSnapshot,
        salesforceReportPref,
        onSelectView: applySavedView,
        onClearAllFilters: handleClearAllFilters,
      });
    }
  }, [
    onReportStateChange,
    activeFilterCount,
    visibleColumnCount,
    hasUnsavedChanges,
    activeViewName,
    currentConfigSnapshot,
    salesforceReportPref,
    applySavedView,
    handleClearAllFilters,
  ]);

  return (
    <ExclusiveColumnFilterContext.Provider value={exclusiveFilterValue}>
    <Box className="mm-na-report-table-wrapper">
      <Box className="mm-na-report-filters">
        <Box className="mm-na-report-filters__pills">
          <FilterPill
            type="date"
            label="Close Date"
            value={closeDateRange}
            onChange={(val: any) => {
              if (val && typeof val === "object" && !Array.isArray(val)) {
                setCloseDateRange(val as DateRangeValue);
              } else {
                setCloseDateRange({ preset: "", startDate: "", endDate: "" });
              }
            }}
            showClearIcon={false}
          />
          {selectedArrAmount?.value !== undefined && Boolean(selectedArrAmount?.operator) && (
            <FilterPill
              type="number"
              label="ARR Amount"
              value={selectedArrAmount}
              displayValue={`${selectedArrAmount.operator === "gt" ? ">" : "<"} $${selectedArrAmount.value?.toLocaleString("en-US")}`}
              onChange={(val) => {
                setSelectedArrAmount(val as NumberFilterValue | undefined);
              }}
              showClearIcon
              useColumnFilter
              columnKey="arrAmount"
            />
          )}
          {createdDateRange?.startDate && createdDateRange?.endDate && (
            <FilterPill
              type="date"
              label="Created Date"
              value={createdDateRange}
              onChange={(val: any) => {
                if (val && typeof val === "object" && !Array.isArray(val)) {
                  setCreatedDateRange(val as DateRangeValue);
                } else {
                  setCreatedDateRange({ preset: "", startDate: "", endDate: "" });
                }
              }}
              showClearIcon={true}
              parent_page="report_page"
            />
          )}
          {[
            { label: "Opportunity Name", columnKey: "opportunityName", options: opportunityNameOptions, value: selectedOpportunityName, setValue: setSelectedOpportunityName },
            { label: "Market POD Lead", columnKey: "marketPodLead", options: marketPodLeadOptions, value: selectedMarketPodLead, setValue: setSelectedMarketPodLead },
            { label: "Opportunity Owner", columnKey: "opportunityOwner", options: opportunityOwnerOptions, value: selectedOpportunityOwner, setValue: setSelectedOpportunityOwner },
            { label: "Account Name", columnKey: "accountName", options: accountNameOptions, value: selectedAccountName, setValue: setSelectedAccountName },
            { label: "India SP", columnKey: "indiaSp", options: indiaSpOptions, value: selectedIndiaSp, setValue: setSelectedIndiaSp },
            { label: "SP POD Lead", columnKey: "spPodLead", options: spPodLeadOptions, value: selectedSpPodLead, setValue: setSelectedSpPodLead },
            { label: "AE Team", columnKey: "aeTeam", options: aeTeamOptions, value: selectedAeTeams, setValue: setSelectedAeTeams },
            { label: "Stage", columnKey: "stage", options: stageOptions, value: selectedStages, setValue: setSelectedStages },
            { label: "Manager Forecast", columnKey: "managerForecast", options: managerForecastOptions, value: selectedManagerForecasts, setValue: setSelectedManagerForecasts },
            { label: "Deal Type", columnKey: "dealType", options: dealTypeOptions, value: selectedDealTypes, setValue: setSelectedDealTypes },
            { label: "Opportunity Record Type", columnKey: "opportunityRecordType", options: recordTypeOptions, value: selectedRecordTypes, setValue: setSelectedRecordTypes },
            { label: "L1 RYG", columnKey: "l1Ryg", options: l1RygOptions, value: selectedL1Rygs, setValue: setSelectedL1Rygs },
            { label: "Buyer Mode", columnKey: "buyerMode", options: buyerModeOptions, value: selectedBuyerModes, setValue: setSelectedBuyerModes },
            { label: "Opportunity Source", columnKey: "opportunitySource", options: opportunitySourceOptions, value: selectedOpportunitySources, setValue: setSelectedOpportunitySources },
          ]
            .filter((pill) => pill.value.length > 0)
            .map((pill) => (
            <FilterPill
              key={pill.columnKey}
              label={pill.label}
              columnKey={pill.columnKey}
              options={pill.options}
              value={pill.value}
              onChange={(val: any) => {
                if (Array.isArray(val)) pill.setValue(val);
                else if (typeof val === "string") pill.setValue(val ? [val] : []);
                else pill.setValue([]);
              }}
              onSearchChange={
                  ["opportunityName", "accountName", "marketPodLead", "opportunityOwner", "indiaSp", "spPodLead"].includes(pill.columnKey)
                    ? (query) => handleSearchChange(pill.columnKey, query)
                    : undefined
                }
                isLoading={
                  ["opportunityName", "accountName", "marketPodLead", "opportunityOwner", "indiaSp", "spPodLead"].includes(pill.columnKey)
                    ? isColumnPresearchLoading(pill.columnKey)
                    : false
                }
              showClearIcon={true}
              useColumnFilter
            />
          ))}
        </Box>
        <Box className="mm-na-report-filters__columns">
          <ColumnFilter
            currentValue={columnVisibility}
            onApply={(val) => setColumnVisibility(val as Record<string, boolean>)}
            options={columnPickerOptions}
            type="toggle"
            columnKey="columnVisibility"
            onOpen={() => setOpenFilterColumn("columnVisibility")}
            onClose={() => setOpenFilterColumn(null)}
            showSections={{
              search: false,
              sort: false,
            }}
            enableColumnReordering={true}
            onReorder={(newOrder: string[]) => setColumnOrder(newOrder)}
          />
        </Box>
      </Box>
      <Box className="report-table-container">
      <DataTable
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        pageSize={pageSize}
        initialSort={sortingState}
        onPageSizeChange={handlePageSizeChange}
        onPaginationChange={handlePageChange}
        onSortingChange={handleSortingChange}
        timeRange=""
        calendarId={null}
        rowHoverEnabled={true}
        showSpinner={showSpinner}
        pinnedColumns={pinnedColumns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        enableColumnResizing={true}
        state={{ columnOrder: reconciledColumnOrder }}
        onColumnOrderChange={(newOrder: string[]) => {
          setColumnOrder(newOrder);
        }}
        showVerticalDividers={true}
        muiTableBodyRowProps={({ row }: any) => {
          const isRowEditing = editingInfo?.rowId === row.original.opportunityId;
          return {
            sx: {
              ...(isRowEditing && {
                "& td, & th": {
                  backgroundColor: "#EFEBFB !important",
                },
                "& td[data-pinned='true']:before": {
                  backgroundColor: "#EFEBFB !important",
                },
              }),
            },
          };
        }}
        muiTableBodyCellProps={({ cell, row }: any) => {
          const isRowEditing = editingInfo?.rowId === row.original.opportunityId;
          const isCellEditing = isRowEditing && editingInfo?.columnId === cell.column.id;
          return {
            sx: {
              ...(isCellEditing && {
                backgroundColor: "#CEC1F2 !important",
              }),
            },
          };
        }}
      />
      </Box>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={isUpdating ? undefined : handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            padding: "16px",
            width: "360px",
            borderRadius: "12px",
            boxShadow: "0px 8px 24px rgba(96, 54, 214, 0.12)",
            border: "1px solid #E5E5E9",
            zIndex: 1400,
          },
        }}
      >
        {editingInfo && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1E1E24" }}>
              Edit {editingInfo.columnName}
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              value={editingInfo.value}
              onChange={(e) =>
                setEditingInfo((prev) => (prev ? { ...prev, value: e.target.value } : null))
              }
              variant="outlined"
              autoFocus
              disabled={isUpdating}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "13.5px",
                  borderRadius: "8px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#6036D6",
                  },
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", mt: 0.5 }}>
              <Button
                variant="text"
                onClick={handleClosePopover}
                disabled={isUpdating}
                sx={{
                  color: "#6C6C78",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSavePopover}
                disabled={isUpdating}
                sx={{
                  backgroundColor: "#6036D6",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "13px",
                  borderRadius: "6px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#4C28B5",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(96, 54, 214, 0.5)",
                    color: "#FFFFFF",
                  },
                }}
              >
                {isUpdating ? <CircularProgress size={16} color="inherit" /> : "Save"}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
    </ExclusiveColumnFilterContext.Provider>
  );
};

export default MMNAReport;
